import React, { useState, useEffect, ChangeEvent } from 'react';
import { Box, Button, Chip, Container, Dialog, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, Typography, alpha } from '@mui/material';
import { deleteDoc, doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MedicalPaperRequest, MedicalPaperRequestStatus } from '../../../types/MedicalPaperRequest';
import { updateMedicalPaperRequestStatus } from '../../../utils/updateMedicalPaperRequest';
import medicalPaperRequestConverter from '../../../converters/medicalPaperRequestConverter';
import { RunCircleRounded, MopedRounded, CheckCircleRounded, DeleteRounded, EditRounded, OpenInNewRounded } from '@mui/icons-material';
import { MedicalPaperRequestInfoBox } from './MedicalPaperRequestInfoBox';
import { FileBox } from '../FileBox';
import { DateTime } from 'luxon';
import { type } from '@testing-library/user-event/dist/type';
import { storage } from 'firebase-admin';
import { uploadString, ref, getStorage } from 'firebase/storage';
import { MedicalPaperRequestForm } from './MedicalPaperRequestForm';
import { SecureComponent } from '../../../security/SecureComponent';
import { hasPermission } from '../../../security/Permission';
import { useAppSelector } from '../../../store';

export const MedicalPaperRequestPage = () => {
  const storage =  getStorage();
  const db = getFirestore();
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);

  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { state } = useLocation();

  const [medicalPaperRequest, setMedicalPaperRequest] = useState<MedicalPaperRequest>((state as any)?.medicalPaperRequest ?? undefined);
  const [disableDelete, setDisableDelete] = useState(false);

  const [showForm, setShowForm] = useState(false);
  
  const updateStatus = (status: MedicalPaperRequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updateMedicalPaperRequestStatus(medicalPaperRequest, status).then(() => {
        getDoc(doc(db, `Employees/${medicalPaperRequest.idNumber}/MedicalPaperRequests/${id}`).withConverter(medicalPaperRequestConverter)).then(docSnapshot => {
          if (docSnapshot.exists()) {
            setMedicalPaperRequest(docSnapshot.data());
          }
        });
        enqueueSnackbar(`Medical paper request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  const handleDeleteOnClick = () => {
    if (window.confirm('Are you sure you want to delete this medical paper request?')) {
      setDisableDelete(true);
      deleteDoc(doc(db, `Employees/${medicalPaperRequest.idNumber}/MedicalPaperRequests/${medicalPaperRequest.id}`))
        .then(() => {
          enqueueSnackbar('Medical papers request has been deleted.', { variant: 'success' });
        })
        .finally(() => {
          setDisableDelete(false);
          setTimeout(() => navigate(-1), 250);
      });
    }
  };

  const openForm = () => setShowForm(true);
  const closeForm = () => setShowForm(false);
  const handleSetProcessingOnClick = () => updateStatus('Processing');
  const handleSetAvailableOnClick = () => updateStatus('Available');
  const handleSetCompletedOnClick = () => updateStatus('Completed');
  
  const handleFormOnSave = (m: MedicalPaperRequest) => {
    setMedicalPaperRequest(m);
    setShowForm(false);
  };

  const upload = async (fileName: string, data: string) => {
    const idNumber = medicalPaperRequest.idNumber;
    const id = medicalPaperRequest.id;
    const dateString = DateTime.utc().toFormat("yyyyMMdd_hhmmss");
    const file = 'File_' + id + '_' + dateString + '_' + fileName;

    const dest = `${idNumber}/MedicalPaperRequests/${file}`;
    return await uploadString(ref(storage, dest),  data, 'data_url')
      .then(async () => {
        const url = "https://firebasestorage.googleapis.com/v0/b/nadafa-ems.appspot.com/o/" + encodeURIComponent(dest) + "?alt=media";
        await setDoc(doc(db, `Employees/${idNumber}/MedicalPaperRequests/${id}`), { file: url }, { merge: true });
        return url;
      })
      .then((url) => {
        // Update selected vacation
        setMedicalPaperRequest({ ...medicalPaperRequest, file: url });
        enqueueSnackbar('File has been uploaded!', { variant: 'success' });
        return true;
      })
      .catch(r => {
        enqueueSnackbar('An error has occured. ' + r, { variant: 'error' });
        return false;
      });
  };

  useEffect(() => {
    if (!state || !(state as any)?.medicalRequest) {
      getDoc(doc(db, `MedicalPaperRequests/${id}`).withConverter(medicalPaperRequestConverter)).then(docSnapshot => {
        if (docSnapshot.exists()) {
          setMedicalPaperRequest(docSnapshot.data());
        }
      });
    }
  }, []);

  if (!medicalPaperRequest) {
    return (
      <Typography variant="caption">Please wait...</Typography>
    );
  }
  
  return (
    <>
      <Container disableGutters>
        <Stack spacing={3} direction="row" alignItems="center" mb={2}>
          <Chip label={medicalPaperRequest.idNumber} />
          <Typography variant="h6">{medicalPaperRequest.name}</Typography>
          <SecureComponent permissions={['update.medicalPaperRequests', 'delete.medicalPaperRequests']}>
            <Stack direction='row' spacing={1} sx={{ display: 'flex', flexGrow: 1, justifyContent: 'right' }}>
              <SecureComponent permissions={['update.medicalPaperRequests']}>
                <Button startIcon={<EditRounded />} color="warning" onClick={openForm}>EDIT</Button>
              </SecureComponent>
              <SecureComponent permissions={['delete.medicalPaperRequests']}>
                <Button startIcon={<DeleteRounded />} color="error" disabled={disableDelete} onClick={handleDeleteOnClick}>DELETE</Button>
              </SecureComponent>
            </Stack>
          </SecureComponent>
        </Stack>
        <Paper>
          <MedicalPaperRequestInfoBox medicalPaperRequest={medicalPaperRequest} />
        </Paper>
        <SecureComponent permissions={['update.medicalPaperRequests']}>
          <Stack mt={1} spacing={1} direction="row" justifyContent="right">
            <Button 
              color="warning" 
              startIcon={<RunCircleRounded />} 
              disabled={medicalPaperRequest.status !== 'New'}
              onClick={handleSetProcessingOnClick}
            >
              Processing
            </Button>
            <Button 
              color="secondary" 
              startIcon={<MopedRounded />}
              disabled={medicalPaperRequest.status !== 'New' && medicalPaperRequest.status !== 'Processing'}
              onClick={handleSetAvailableOnClick}
            >
              For Pickup
            </Button>
            <Button 
              color="success" 
              startIcon={<CheckCircleRounded />} 
              disabled={medicalPaperRequest.status === 'Completed' || medicalPaperRequest.status === 'Cancelled'}
              onClick={handleSetCompletedOnClick}
            >
              Completed
            </Button>
          </Stack>
        </SecureComponent>
        {medicalPaperRequest.status !== 'New' && (
          <Stack mt={3} spacing={1}>
            <FileBox label="File" file={medicalPaperRequest.file} onUpload={upload} canUpload={hasPermission(authState, [], ['update.medicalPaperRequests'])} />
          </Stack>
        )}
      </Container>
      <SecureComponent permissions={['update.medicalPaperRequests']}>
        <Dialog open={showForm} onClose={closeForm} fullWidth>
          <DialogTitle>Update Medical Paper Request</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 0 }}>
            {medicalPaperRequest && (
              <MedicalPaperRequestForm medicalPaperRequest={medicalPaperRequest} onClosed={closeForm} onSaved={handleFormOnSave} />
            )}
          </DialogContent>
        </Dialog>
      </SecureComponent>
    </>
  );
};
